import React, { useState, useMemo } from 'react'
import { goodsShelfStore } from '../../store/goods-shelf'
import { IMallGoods } from '@/model/types/mall'
import { IPagination } from '@/model/types/common'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import { getBEMCls } from '@/utils/classname'
import { showLayer, layerManage } from '@/utils/layer'
import { intervalPrice } from '@/utils/goods'
import Tooltip from 'antd/es/tooltip'
import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { WithScrollLoad, AutoResizer } from '@/utils/utils-widget'
import { observer } from 'mobx-react'

import './good-table.scss'
import { Row } from '@/components/layout/layout'
import Button from '@/components/button/button'
import { Modal } from '@/components/dialog/modal'
import $msg from '@/utils/message'
import { PricesPopper } from '@/views/main/modules/goods-manage/components/prices/prices-popper/prices-popper'
import MallGoodsTableEdit from './good-table-edit'
import { mallGoodsStore } from '../../store/mall-goods'
import { MALL_DARG_TYPE } from '../../../mall-shelf'
import { useDrag } from 'react-dnd'
import TextPopover from '@/components/text-popover/text-popover'
import { getQiniuImgUrl } from '@/config/qiniu'
import { get } from '@/utils'
export interface MallGoodsTableList {
    list: FormatMallGood[]
    pagination: IPagination
}

export interface FormatMallGood extends IMallGoods {
    top: boolean
}

const MallGoodsTable = observer(() => {
    const cls = (...args: string[]) => getBEMCls('mall-table', ...args)

    const [checkList, setCheckList] = useState<IMallGoods[]>([])
    const [selectKeys, setSelectKeys] = useState<any[]>([])

    const [, drag, preview] = useDrag({
        item: {
            type: MALL_DARG_TYPE,
            goods_ids: selectKeys,
        },
        canDrag: () => !!selectKeys.length && !!goodsShelfStore.mallShelfConfig?.independent_shelf_opened,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const showHotSale = useMemo(() => !goodsShelfStore.groupId && goodsShelfStore.mallShelfConfig?.hot_sold, [
        goodsShelfStore.groupId,
        goodsShelfStore.mallShelfConfig,
    ])

    const requestHandle = useRequest<
        MallGoodsTableList,
        {
            mall_goods_list: IMallGoods[]
            pagination: IPagination
        }
    >(
        val => {
            return (
                // 在分组未完成加载前不加载表格数据
                goodsShelfStore.groupId !== undefined
                    ? $post({
                          url: showHotSale ? '/mall/hotsalegoods' : '/mall/getmallgoods',
                          params: showHotSale
                              ? undefined
                              : {
                                    // 如果当前分组不是全部商品，则获取当前分组的商品，如果是全部商品，判断是否进行了分组筛选，都没有就获取全部的商品
                                    mall_group_id: goodsShelfStore.groupId,
                                    mall_secondary_group_id: goodsShelfStore.secondGroupId || undefined,
                                    limit: 20,
                                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                                    keyword: goodsShelfStore.keyWord ? goodsShelfStore.keyWord : undefined,
                                },
                      })
                    : new Promise(() => {})
            )
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            onSuccess: () => {
                setCheckList([])
                setSelectKeys([])
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.mall_goods_list.map(g => {
                        const group = g.groups.find(v => v.group_id === goodsShelfStore.groupId)

                        return {
                            ...g,
                            top: !!(group && group.top_goods === 255),
                        }
                    }),
                    pagination: data.pagination,
                }
            },
            refreshDeps: [
                goodsShelfStore.groupId,
                goodsShelfStore.secondGroupId,
                goodsShelfStore.keyWord,
                goodsShelfStore.groupIds,
                goodsShelfStore.status,
                goodsShelfStore.resetSatus, // 用于重置数据
                goodsShelfStore.updateGroupStatus, // 用于刷新当前分组数据
            ],
        },
    )

    const { data, loading } = requestHandle

    const previewColumns: ColumnsType<IMallGoods> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品标题',
            dataIndex: 'goods',
            ellipsis: {
                showTitle: true,
            },
            render: (v, r) => r.title || r.goods.name,
        },
        {
            title: '销售量',
            dataIndex: 'amount_sold',
            align: 'right',
        },
        {
            title: '库存',
            dataIndex: 'stock',
            align: 'right',
        },
    ]

    const columns: ColumnsType<FormatMallGood> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '图片',
            width: 60,
            align: 'center',
            key: 'img_url',
            render: (value, record) => (
                <img
                    src={getQiniuImgUrl(get(record, 'mall_goods_images[0].img_url', ''))}
                    alt=""
                    width="40"
                    height="40"
                    data-border-radius-5
                    data-margin-top-8
                />
            ),
        },
        {
            title: '商品标题',
            dataIndex: 'goods',
            width: 200,
            render: (v, r) => <TextPopover width={180} title={r.title} keyWord={goodsShelfStore.keyWord} />,
        },
        {
            title: '商品名',
            width: 200,
            dataIndex: 'goods',
            render: (v, r) => <TextPopover width={180} title={r.goods.name} keyWord={goodsShelfStore.keyWord} />,
        },
        {
            title: '售价',
            dataIndex: 'pricing_methods',
            align: 'right',
            width: 120,
            render: (pricingMethod, record) => {
                const pricingMethods = record.pricing_methods.length
                    ? record.pricing_methods
                    : record.goods.pricing_methods
                const firstPricingMethod = pricingMethods[0]

                return (
                    <div>
                        {pricingMethods.length > 1 || firstPricingMethod.sale_prices.length > 1 ? (
                            <Tooltip
                                overlayClassName="custom-popover"
                                title={() => (
                                    <PricesPopper
                                        stockUnit={record.goods.stock_unit}
                                        pricingMethod={pricingMethods}
                                    ></PricesPopper>
                                )}
                            >
                                <div data-cursor-pointer>
                                    ¥{intervalPrice(pricingMethods).min}~{intervalPrice(pricingMethods).max}
                                </div>
                            </Tooltip>
                        ) : (
                            <div>
                                <div>
                                    ¥{firstPricingMethod.sale_prices[0].sale_price}/
                                    {firstPricingMethod.sale_prices[0].sale_unit.name}
                                </div>
                                {firstPricingMethod.mall_display?.underline_price !== null &&
                                    firstPricingMethod.mall_display?.underline_price !== undefined && (
                                        <div data-font-size-12 data-color-999>
                                            划线价 <s>¥{firstPricingMethod.mall_display.underline_price}</s>
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            title: '销售量',
            dataIndex: 'amount_sold',
            align: 'right',
        },
        {
            title: '库存',
            dataIndex: 'stock',
            align: 'right',
        },
        {
            title: '操作',
            key: 'action',
            width: 94,
            align: 'left',
            render: (value, record) => {
                return (
                    <MallGoodsTableEdit
                        showTop={
                            !showHotSale &&
                            !goodsShelfStore.keyWord &&
                            mallGoodsStore.defaultGroupId !== goodsShelfStore.groupId
                        }
                        goodId={record.id}
                        top={record.top}
                    />
                )
            },
        },
    ]

    const removeGoods = () => {
        $post({
            url: '/mall/onekeyremovemallgoods',
            params: {
                mall_goods_id_list: selectKeys,
            },
        })
            .then(() => {
                $msg('success', '移出成功')
                goodsShelfStore.setUpdateGroupStatus(!goodsShelfStore.updateGroupStatus)
                setSelectKeys([]) // 重置选中数组
            })
            .catch(e => $msg('error', e.message))
    }

    return (
        <>
            <div ref={drag} className={cls('container')}>
                <AutoResizer>
                    {({ height }) => {
                        return (
                            <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                                <Table
                                    rowKey={record => record.id}
                                    loading={loading || requestHandle.loadingMore || void 0}
                                    rowSelection={{
                                        type: 'checkbox',
                                        selectedRowKeys: selectKeys,
                                        onChange: (selectedRowKeys, selectedRows) => {
                                            setSelectKeys(selectedRowKeys)
                                            setCheckList([...selectedRows])
                                        },
                                    }}
                                    bordered
                                    pagination={false}
                                    columns={columns}
                                    dataSource={data?.list}
                                    scroll={{ x: 'max-content', y: height - 50 }}
                                />
                            </WithScrollLoad>
                        )
                    }}
                </AutoResizer>
                <Row className={cls('container', 'bottom')}>
                    <div data-font-size-14 data-margin-left-16 data-margin-right-32>
                        已选
                        <div data-inline data-color-theme data-margin-h-4>
                            {checkList.length}
                        </div>
                        个商品
                    </div>
                    {!!checkList.length && (
                        <Button
                            onClick={() => {
                                if (!goodsShelfStore.mallShelfConfig?.independent_shelf_opened)
                                    $msg('warning', '请先开启独立货架')
                                showLayer(
                                    <Modal
                                        headerStyles={{ width: '440px' }}
                                        isShow
                                        type="danger"
                                        style={{ height: '156px' }}
                                        onClose={() => layerManage.pop()}
                                        onCancel={() => layerManage.pop()}
                                        onConfirm={() => {
                                            layerManage.pop()
                                            removeGoods()
                                        }}
                                    >
                                        <div>
                                            <div data-font-size-20 data-font-weight-600 data-color-000>
                                                确定全部移出货架吗？
                                            </div>
                                            <div data-font-size-14 data-color-999 data-margin-top-8>
                                                移出货架后该商品将不能在商城销售
                                            </div>
                                        </div>
                                    </Modal>,
                                )
                            }}
                            types="error"
                        >
                            移出货架
                        </Button>
                    )}
                </Row>
            </div>
            <div ref={preview} className={cls('preview')}>
                <Table
                    rowKey={record => record.id}
                    pagination={false}
                    bordered
                    columns={previewColumns}
                    dataSource={checkList}
                />
                <div className={cls('badge')}>{selectKeys.length}</div>
            </div>
        </>
    )
})

export default MallGoodsTable
